import { ApolloClient } from '@apollo/client';
import { Badge } from '@mui/material';
import {
  type GridAggregationModel,
  GridToolbarContainer,
  gridColumnDefinitionsSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import { RiSettings2Line } from '@remixicon/react';
import ExpandAllButton from 'src/components/atoms/buttons/ExpandAllButton';
import Flex from 'src/components/atoms/Flex';
import { AgButton } from 'src/components/ui';
import { VIEW_PAGES } from 'src/managers/viewConfig.store';
import { GridLiveRegion, useGridSelectionAnnouncement } from '../a11y';
import type { GridApiRef } from '../DataGrid';
import { type ShouldRenderBulkActionToolbar, useGridBulkActions } from '../selection';
import type { BaseGridStateWithHelpers } from '../shared/baseGridTypes';
import type { ServerGridFilter } from '../shared/gridFilterTypes';
import BulkActionToolbar from './BulkActionToolbar';
import ClearableSearch from './ClearableSearch';
import CustomizeTable from './CustomizeTable';
import AddFilter from './filters/AddFilter';
import FilterList from './filters/FilterList';
import { useAlertIndicator, useQuickFilterCounts } from './filters/tools';
import ToolbarButton from './ToolbarButton';
import { gridSubjectLabel, gridToolbarLabel } from './toolbarLabel';
import ViewConfigurationMenu from './ViewConfigurationMenu';

export type GridToolbarQuickFilter = {
  label: string;
  filters?: ServerGridFilter[];
  getAlertValue?: (client: ApolloClient<object>) => Promise<number | string | null>;
};

type GridToolbarV2Props = {
  enhancedGridState: BaseGridStateWithHelpers;
  viewPage?: VIEW_PAGES;
  /** Forced onto the grid's aggregation on every saved-view restore (see ViewConfigurationMenu). */
  aggregationModel?: GridAggregationModel;
  /** REQUIRED: drives bulk-action selection and expand-all; the column selectors read the grid context api. */
  apiRef: GridApiRef;
  quickFilterCountModel?: string;
  entityName?: string;
  entityNamePlural?: string;
  bulkActions?: React.ReactNode;
  showExpandAllButton?: boolean;
  expandAllMode?: 'group' | 'detailPanel';
  selectionAdornment?: (selectedRows: Record<string, unknown>) => React.ReactNode;
  toolbarActions?: React.ReactNode;
  toolbarActionsRight?: React.ReactNode;
  groupByButton?: React.ReactNode;
  groupByList?: React.ReactNode;
  renderSelectionAdornment?: (selectedRows: Record<string, unknown>) => React.ReactNode;
  shouldRenderBulkActionToolbar?: ShouldRenderBulkActionToolbar;
  renderQuickFilterRight?: React.ReactNode;
  /** Test ids forwarded to the inner toolbar atoms — keeps shared atoms generic and lets each list page tag its own UI. */
  testIds?: {
    search?: string;
    addFilter?: string;
    addFilterApply?: string;
    addFilterCheckboxPrefix?: string;
    filterChipPrefix?: string;
    viewDropdown?: string;
    customizeTable?: string;
    customizeColumnPrefix?: string;
    customizeEnableAll?: string;
    customizeReset?: string;
    quickFilterPrefix?: string;
    expandAll?: string;
  };
};

export default function GridToolbarV2({
  enhancedGridState,
  viewPage,
  aggregationModel,
  apiRef,
  quickFilterCountModel,
  entityName,
  entityNamePlural,
  showExpandAllButton,
  expandAllMode,
  bulkActions,
  toolbarActions,
  toolbarActionsRight,
  groupByButton,
  groupByList,
  renderSelectionAdornment,
  shouldRenderBulkActionToolbar,
  renderQuickFilterRight,
  testIds,
}: GridToolbarV2Props) {
  const counts = useQuickFilterCounts(quickFilterCountModel, enhancedGridState.quickFilterOptions);
  const contextApiRef = useGridApiContext();
  const columns = useGridSelector(contextApiRef, gridColumnDefinitionsSelector);
  const bulk = useGridBulkActions({
    apiRef,
    enhancedGridState,
    hasBulkActions: !!bulkActions,
    entityName,
    entityNamePlural,
    shouldRender: shouldRenderBulkActionToolbar,
  });

  const selectionAnnouncement = useGridSelectionAnnouncement({
    selectedCount: bulk.selectedCount,
    entityName,
    entityNamePlural,
  });

  return (
    <GridToolbarContainer
      role="toolbar"
      aria-label={gridToolbarLabel(entityNamePlural, viewPage)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        mb: '20px',
        pb: 0,
        mx: -0.5,
        '@media print': { display: 'none' },
      }}
    >
      <Flex gap={1.5} width="100%" alignItems="center">
        <ClearableSearch
          searchState={enhancedGridState.searchText}
          onSearch={enhancedGridState.setSearch}
          ariaLabel={`Search ${gridSubjectLabel(entityNamePlural, viewPage).toLocaleLowerCase()}`}
          data-testid={testIds?.search}
        />
        {groupByButton}
        <AddFilter
          columns={columns}
          enhancedGridState={enhancedGridState}
          data-testid={testIds?.addFilter}
          applyTestId={testIds?.addFilterApply}
          checkboxTestIdPrefix={testIds?.addFilterCheckboxPrefix}
        />

        {viewPage && (
          <ViewConfigurationMenu
            key={viewPage}
            page={viewPage}
            enhancedGridState={enhancedGridState}
            aggregationModel={aggregationModel}
            data-testid={testIds?.viewDropdown}
          />
        )}

        <Flex flexGrow={1} />

        {toolbarActions}
        {showExpandAllButton && (
          <ExpandAllButton apiRef={apiRef} mode={expandAllMode} data-testid={testIds?.expandAll} />
        )}
        <ToolbarButton icon={<RiSettings2Line size={16} />} data-testid={testIds?.customizeTable}>
          {(close) => (
            <CustomizeTable
              closeMenu={close}
              columnTestIdPrefix={testIds?.customizeColumnPrefix}
              enableAllTestId={testIds?.customizeEnableAll}
              resetTestId={testIds?.customizeReset}
            />
          )}
        </ToolbarButton>
        {toolbarActionsRight}
      </Flex>

      {groupByList}
      <FilterList
        columns={columns}
        enhancedGridState={enhancedGridState}
        chipTestIdPrefix={testIds?.filterChipPrefix}
      />

      {bulk.isVisible && (
        <BulkActionToolbar bulk={bulk} bulkActions={bulkActions} renderSelectionAdornment={renderSelectionAdornment} />
      )}

      <GridLiveRegion announcement={selectionAnnouncement} data-testid="grid-selection-announcement" />

      {enhancedGridState.quickFilterOptions && (
        <Flex gap={1} width="100%" alignItems="center">
          {enhancedGridState.quickFilterOptions.map((filter) => {
            return (
              <GridQuickFilter
                key={filter.label}
                filter={filter}
                enhancedGridState={enhancedGridState}
                counts={counts}
                testIdPrefix={testIds?.quickFilterPrefix}
              />
            );
          })}
          {renderQuickFilterRight && (
            <>
              <Flex flexGrow={1} />
              {renderQuickFilterRight}
            </>
          )}
        </Flex>
      )}
    </GridToolbarContainer>
  );
}

const toQuickFilterSlug = (label: string) => label.trim().toLowerCase().replace(/\s+/g, '-');

const GridQuickFilter = ({
  filter,
  enhancedGridState,
  counts,
  testIdPrefix,
}: {
  filter: GridToolbarQuickFilter;
  enhancedGridState: BaseGridStateWithHelpers;
  counts: Record<string, number>;
  testIdPrefix?: string;
}) => {
  const selected = filter.label === enhancedGridState.quickFilterOption?.label;
  const alertIndicator = useAlertIndicator(filter);
  const dataTestId = testIdPrefix ? `${testIdPrefix}-${toQuickFilterSlug(filter.label)}` : undefined;

  return (
    <Badge color="info" variant="standard" invisible={false} badgeContent={alertIndicator}>
      <AgButton
        key={filter.label}
        variant={selected ? 'selected' : 'white'}
        shape="pill"
        className={selected ? 'font-bold' : ''}
        onPress={() => enhancedGridState.setQuickFilterOption(filter)}
        data-testid={dataTestId}
      >
        {filter.label} {filter.label in counts ? `(${counts[filter.label]})` : ''}
      </AgButton>
    </Badge>
  );
};
